// LRPO-5797 — Shows the outcome of the territory builder autosave next to the
// Save Changes button. The autosave_notice_controller (LRPO-5669) already renders
// the red error banner and clears itself on success, so a successful save produced
// no feedback at all — this controller is the positive half.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.element.setAttribute("aria-live", "polite")

    // ★★ LRPO-5797 — TWO CONTROLLERS WRITE THIS BADGE: the name autosave and the
    // form autosave, both dispatching on `document`. Rendering whichever event
    // arrived last meant a successful form tick painted "All changes saved" over
    // a name that had just been REJECTED — the territory was unsaved and the
    // badge said otherwise. A success from one saver says nothing about the
    // other, so failures are tracked per source and the badge only goes green
    // when none are outstanding.
    this.failedSources = new Set()

    // Events dispatched without a source still work; they share one key.
    const sourceOf = (event) => (event && event.detail && event.detail.source) || "default"

    this._onAutosaving = (event) => {
      // ⚠ Do NOT clear failures here. The form ticking again while the name is
      // still rejected is precisely QA's sequence.
      if (this.failedSources.size > 0) return
      this._render("saving")
    }

    this._onAutosaved = (event) => {
      this.failedSources.delete(sourceOf(event))
      this._render(this.failedSources.size > 0 ? "failed" : "saved")
    }

    this._onAutosaveFailed = (event) => {
      this.failedSources.add(sourceOf(event))
      this._render("failed")
    }

    document.addEventListener("territory:autosaving", this._onAutosaving)
    document.addEventListener("territory:autosaved", this._onAutosaved)
    document.addEventListener("territory:autosave-failed", this._onAutosaveFailed)
  }

  disconnect() {
    document.removeEventListener("territory:autosaving", this._onAutosaving)
    document.removeEventListener("territory:autosaved", this._onAutosaved)
    document.removeEventListener("territory:autosave-failed", this._onAutosaveFailed)
  }

  // className is ASSIGNED (not added) so that switching states removes the
  // previous state's colour classes. With add-only handling, success and danger
  // would sit on the element at once and Bootstrap would paint "All changes
  // saved" red instead of green.
  _render(state) {
    const config = {
      saving: {
        className: "autosave-status text-nowrap badge text-bg-secondary",
        iconClass: "bi bi-arrow-repeat me-1",
        label: "Saving…"
      },
      saved: {
        className: "autosave-status text-nowrap badge text-bg-success",
        iconClass: "bi bi-check-circle-fill me-1",
        label: "All changes saved"
      },
      failed: {
        className: "autosave-status text-nowrap badge text-bg-danger",
        iconClass: "bi bi-exclamation-triangle-fill me-1",
        label: "Not saved"
      }
    }[state]

    this.element.className = config.className
    this.element.replaceChildren()

    const icon = document.createElement("i")
    icon.className = config.iconClass
    icon.setAttribute("aria-hidden", "true")
    this.element.append(icon)

    this.element.append(document.createTextNode(config.label))
  }
}
